import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button, Caption1, MessageBar, MessageBarActions, MessageBarBody, Spinner, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { MAX_SAVED_FILTERS, ROSTER_NO_AREA, findVirginiaPlace, isComplianceTracked, sameServiceArea, type Person, type ReadinessState, type RosterFilterPreference, type SavedRosterFilter } from '@vigeo/shared';
import { shellMedia, shellMetrics, tokens, useShellTier } from '@vigeo/ui';
import { usePeopleReadiness } from '@/features/candidates';
import { LiveMap, PinLetter, countWords, letterOrder, listedPlaceWords, liveMapLetters, placeWords, type LiveMapPlace } from '@/features/places';
import {
  ActiveFilters, LIMIT_MESSAGE, NO_JOB_TITLE, OfficeStaffLine, areaGoneMessage, RememberedStrip, RosterCompactFilters, RosterFilterRow, RosterViewSwitch, SaveFiltersDialog, SavedFiltersMenu, StaffDialog, StaffSideList, StaffTable, choosePlace, complianceLabel, coverageCounts,
  hasNoServiceArea, holdsOnlyNonClinical, jobTitleOptions, matchesRosterFilters, noRosterFilters, otherFiltersOn, placesOf, preferenceIsOn, readStored, rosterViewOf, samePreference, serviceAreasOf, showEveryone, showNoPlace, sortByName, staffHeading, staleMessage, toPreference,
  seesPatients, useAtLeastWide, useRosterPreferences, withParts, withRosterView, withoutPlaces, type CompactFiltersPanel, type NameSort, type RosterControls, type RosterFilters, type RosterView,
} from '@/features/roster';
import { readinessBandOrder, statePresenters } from '@/features/readiness';
import { QuickLookPanel, profilePath, roleLabels, separatedNoticeOf, useRoleOptions } from '@/features/employee-profile';
import { fetchPeople } from '@/lib/api';
import { fetchAuthState } from '@/lib/auth';
import { complianceAndDocumentsShown, sectionOn } from '@/lib/sections';

/**
 * The roster: people the agency EMPLOYS, which is a different question from the People directory.
 *
 * People answers "everyone we track, from first application to separation". Roster answers "who can
 * be assigned work right now", so it holds `active` and `inactive` only — a candidate is not staff,
 * and a separated employee is not on the roster. Frame 06b 0.1 states the split in its own subtitle
 * ("42 active employees · 2 inactive (on leave)"), and the readiness bands below come from the same
 * card.
 *
 * Readiness is derived per person from the same checklist the Person Overview reads, so a number
 * here can never disagree with the record behind it.
 */
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL },
  // The title takes the room the switch and the button leave, so those two sit together at the end of the row (frame S1). It starts
  // narrow and grows, so on a phone the line under the title wraps and the button stays beside the title (frames T3 and M5).
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0, flexGrow: 1, flexShrink: 1, flexBasis: '10rem' },
  headRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightBold },
  bands: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
    overflow: 'hidden',
    [shellMedia.phone]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    ':first-child': { borderLeft: 'none' },
    [shellMedia.phone]: { ':nth-child(odd)': { borderLeft: 'none' } },
  },
  figure: { fontFamily: tokens.fontFamilyNumeric, fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700, fontWeight: tokens.fontWeightBold },
  label: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    alignItems: 'flex-start',
  },
  centered: { display: 'flex', justifyContent: 'center', paddingBlock: tokens.spacingVerticalXXXL },
  // The staff table is the page's own width (Figma T1 to T3, #673): nothing sits beside it.
  who: { minWidth: 0 },
  /**
   * THE MAP VIEW (#689; Figma page 12, frames M1 to M5). On a desktop the page is as tall as the screen allows below the shell's
   * header (the shell's content box is a column that hands its children its spare height), and the two columns take all of it
   * that the page header leaves: the roster side scrolls inside itself and the map stays put. Never under 480 high, the least the
   * map's legend card needs (#688 item 3); on a window shorter than that the page scrolls instead.
   */
  pageFills: { flexGrow: 1 },
  mapView: { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0, minHeight: '30rem', display: 'grid', gridTemplateColumns: '25rem minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr)', columnGap: tokens.spacingHorizontalL },
  // The page's content under `shellMetrics.mapViewSideWideMin` (frame M4b, 1280 with the menu widened): the roster side is 360.
  mapViewNarrow: { gridTemplateColumns: '22.5rem minmax(0, 1fr)' },
  // The page's content under `shellMetrics.mapViewSideSlimMin` (a tablet, #699): the roster stays beside the map, at 320, so the map keeps its room.
  mapViewSlim: { gridTemplateColumns: '20rem minmax(0, 1fr)' },
  // A phone only (frame M5, #699): the map on top, the roster under it, and the page scrolls. A tablet keeps the two columns.
  mapViewStacked: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
  // A phone's map sets its own height (300, and the legend's row).
  mapBox: { minWidth: 0, minHeight: 0 },
  // Nobody has a place the map can show: no map is made and nobody is asked for one (designer 402's frame M4, #596).
  noMap: {
    height: '100%', minHeight: '12rem', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: tokens.spacingVerticalS, padding: tokens.spacingHorizontalXXL, textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground2, border: `${tokens.strokeWidthThin} dashed ${tokens.colorNeutralStroke1}`, borderRadius: tokens.borderRadiusLarge,
  },
  noMapWords: { maxWidth: '26rem', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  sideNotice: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

export const NO_PLACES_ON_MAP_TITLE = 'No places yet';
export const NO_PLACES_ON_MAP_BODY = 'Nobody on the roster has a place the map can show. Switch to Table, press Edit on a staff member and add a city or a ZIP code. It then appears here as a shaded area, with the people who work there.';
export const NO_PLACES_ON_MAP_NOTE = 'The map appears here as soon as one staff member has a place.';
/** What the map's legend says its numbers count (#804: office staff inflated them). */
export const MAP_COUNTS_PATIENT_FACING = 'Only patient-facing staff are counted. Office staff and staff with no role set are left out.';
/** The legend's words for an area with no shading, now that the counts are patient-facing staff only (#804). */
export const MAP_NOBODY_COUNTED = 'No patient-facing staff match';
const MAP_NOTES: readonly string[] = [MAP_COUNTS_PATIENT_FACING];

const EMPTY: readonly Person[] = [];
const NO_MAP_PLACES: readonly LiveMapPlace[] = [];
const profileHrefOf = (person: Person): string => profilePath(person.id);

/** How long the filters sit still before they are remembered, so a run of ticks is one request, not one each. */
export const LAST_USED_SAVE_DELAY_MS = 600;
const keyOf = (parts: RosterFilterPreference): string => JSON.stringify([[...parts.areas].sort(), [...parts.compliance].sort(), [...parts.statuses].sort(), [...parts.phones].sort()]);
const NONE: RosterFilterPreference = { areas: [], compliance: [], statuses: [], phones: [] };

/**
 * EVERY readiness state, so the bands are a partition and the figures sum to the roster.
 *
 * The ordering itself moved to `readinessBandOrder` when the dashboard's Roster card (#265) began
 * banding the same roster: two copies of "the frame's four, then the rest" is two chances for one
 * screen to start banding differently from the other.
 */
const BANDS: readonly ReadinessState[] = readinessBandOrder;

export function RosterPage() {
  const styles = useStyles();
  const people = useQuery({ queryKey: ['people'], queryFn: () => fetchPeople() });
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;
  /**
   * Which of the two views is showing (#689) is read from the page address and held nowhere else. Changing it adds a step to
   * the browser's history, so Back returns to the view before. The filters below are this page's own state, and the page
   * stays on screen across the switch, so both views show the same people.
   */
  const [search, setSearch] = useSearchParams();
  /**
   * "<name> marked Separated." after the profile's separation sends HR back here (#810). Read once from the history state, then
   * that state is replaced, so a reload or a later Back does not say it again.
   */
  const location = useLocation();
  const [separated, setSeparated] = useState(() => separatedNoticeOf(location.state));
  const noticeCleared = useRef(false);
  useEffect(() => {
    if (separated === null || noticeCleared.current) return;
    noticeCleared.current = true;
    setSearch(search, { replace: true });
  }, [separated, search, setSearch]);
  const view = rosterViewOf(search);
  const changeView = useCallback((next: RosterView) => setSearch(withRosterView(search, next)), [search, setSearch]);
  const tier = useShellTier();
  /** Only on a phone is the Map view one column, the map and then the roster (#699). A tablet keeps the roster beside the map. */
  const stacked = tier === 'mobile';
  /** Measured on the two columns' own box, which is the page's content: the window does not say how wide the menu is. */
  const [measureWide, sideWide] = useAtLeastWide(shellMetrics.mapViewSideWideMin);
  const [measureSlim, sideNotSlim] = useAtLeastWide(shellMetrics.mapViewSideSlimMin);
  const measureMapView = useCallback((element: HTMLElement | null) => { measureWide(element); measureSlim(element); }, [measureWide, measureSlim]);
  const sideWidth = sideWide ? 'wide' : sideNotSlim ? 'narrow' : 'slim';
  // The one filter state (Roster v2): the filter row, the column headers and the tag line all read and write it.
  const [filters, setFilters] = useState<RosterFilters>(noRosterFilters);
  /**
   * Remembered and saved filters (ADR-019, #476). ADMIN ONLY: for anyone else `useRosterPreferences`
   * makes no request and none of the controls below is drawn, so a Viewer never meets a 403.
   */
  const preferences = useRosterPreferences(canEdit);
  const touched = useRef(false);
  const [openHandled, setOpenHandled] = useState(false);
  const [applied, setApplied] = useState<RosterFilterPreference | null>(null);
  const [stripGone, setStripGone] = useState(false);
  const [note, setNote] = useState<{ readonly text: string; readonly key: string } | null>(null);
  /**
   * The "Save these filters" dialog: `null` is closed, otherwise where it was asked for. Inside the Filters button's panel (the Map
   * view, and the Table view on a phone) the control that asked is gone once the panel has closed, so the keyboard goes back to the
   * Filters button; asked from anywhere else it goes back to whatever had it (reviewer 223, #691 finding 2).
   */
  const [saving, setSaving] = useState<'panel' | 'elsewhere' | null>(null);
  const filtersButton = useRef<HTMLButtonElement | null>(null);
  /** Every change a person makes goes through here, so "did they change anything before the remembered filters arrived" is known. */
  /**
   * Only a change to one of the four saved parts counts as "changed something" before the remembered
   * filters arrive. The search box is never part of the remembered set, so typing in it must not cancel them.
   */
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const changeFilters = useCallback((next: RosterFilters) => {
    if (!samePreference(toPreference(next), toPreference(filtersRef.current))) touched.current = true;
    setFilters(next);
  }, []);
  const [sort, setSort] = useState<NameSort>('asc');
  /**
   * Each row of the Map view's side lists the person's job title (#699): the job profile labels the profile and its quick look
   * show, read from the same catalog by the same key. Read only while the Map view is on, and with nothing said until the catalog
   * has arrived, so a row never reads "Role" for a title that is on its way.
   */
  const roleOptions = useRoleOptions(view === 'map');
  const jobProfiles = roleOptions.data?.jobProfiles;
  const titleOf = useCallback((person: Person): string | null => {
    if (jobProfiles === undefined) return null;
    const titles = roleLabels(person, jobProfiles);
    return titles.length === 0 ? NO_JOB_TITLE : titles.join(' · ');
  }, [jobProfiles]);
  /**
   * `undefined` is closed, `null` adds, a person edits that one. The dialog stays MOUNTED and is
   * driven by `open`, never unmounted while open: Fluent hides the rest of the page from assistive
   * technology while a modal is up and only restores it when the dialog closes itself. `opened`
   * changes on every opening, so the form starts fresh each time.
   */
  const [editing, setEditing] = useState<Person | null | undefined>(undefined);
  const [opened, setOpened] = useState(0);
  /** The button that opened the staff dialog: the keyboard goes back to it (a click does not focus a button in Safari on a Mac). */
  const staffOpener = useRef<HTMLElement | null>(null);
  /** The person whose quick look is open over the roster (#585), by id; and the name that opened it, which gets the keyboard back. */
  const [looking, setLooking] = useState<string | null>(null);
  const lookOpener = useRef<HTMLElement | null>(null);
  const openQuickLook = useCallback((person: Person, opener: HTMLElement) => { lookOpener.current = opener; setLooking(person.id); }, []);
  const openDialog = (person: Person | null, opener: HTMLElement) => { staffOpener.current = opener; setOpened((n) => n + 1); setEditing(person); };

  const all = people.data ?? EMPTY;
  const employed = useMemo(() => all.filter((person) => person.lifecycle === 'active' || person.lifecycle === 'inactive'), [all]);
  /**
   * Readiness is derived only for people whose compliance is tracked (ADR-016 §4). Staff added by hand
   * have no records yet, so a derived "not ready" for each would read as a compliance failure when it is
   * missing data; they read "Not tracked yet" instead and are in no band.
   *
   * Where the compliance parts are not drawn (#723) nobody is counted as tracked: no checklist is asked for, and nothing
   * below is banded.
   */
  const showCompliance = complianceAndDocumentsShown();
  const tracked = useMemo(() => (showCompliance ? employed.filter(isComplianceTracked) : EMPTY), [employed, showCompliance]);
  const readiness = usePeopleReadiness(tracked);
  const { stateOf, entryOf } = readiness;

  /**
   * WHO THE LIST SHOWS (Julio, 2026-10-21, #804): staff whose job titles are all administrative or office are hidden by default,
   * with a line that says how many and shows them. Staff with no role set stay, so HR can set it. The list, the map's side list,
   * the map's places and the Job title filter all start from this; the roster's own figures (active, inactive) and the typed
   * areas the server checks saved filters against stay everyone's.
   */
  const [officeShown, setOfficeShown] = useState(false);
  const officeCount = useMemo(() => employed.filter(holdsOnlyNonClinical).length, [employed]);
  const onList = useMemo(() => (officeShown ? employed : employed.filter((person) => !holdsOnlyNonClinical(person))), [employed, officeShown]);
  const officeLine = <OfficeStaffLine count={officeCount} shown={officeShown} onShown={setOfficeShown} />;
  const areas = useMemo(() => serviceAreasOf(employed), [employed]);
  /**
   * The places people hold (ADR-018, #473). Their words are read from the app's own list of places by each place's code
   * (ADR-021 section 2, #594): `placeWords` and `listedPlaceWords` ask nobody, so a name is there on the first draw and
   * a Roster load makes no request for one. A code that is not in the list (one of Google's ids, saved before the list
   * existed) reads "Pick this place again"; it is not offered in the Place filter and the search does not read it.
   */
  const placeIds = useMemo(() => [...new Set(onList.flatMap((person) => placesOf(person).map((place) => place.placeId)))], [onList]);
  const placeOptions = useMemo(
    () => placeIds.flatMap((placeId) => { const label = listedPlaceWords(placeId); return label === undefined ? [] : [{ placeId, label }]; }).sort((a, b) => a.label.localeCompare(b.label)),
    [placeIds],
  );
  /**
   * The letters (decision `ML alpha`): every place somebody holds that is in the app's own list, A to Z by its name as shown. They
   * are worked out here, once, so the staff rows and the map's areas carry the same ones. Every such place gets one: after Z comes
   * AA (ADR-021 section 6). An old code that is not in the list is not on the map and has no letter (Figma M1: an empty circle).
   */
  const placeOrder = useMemo(() => letterOrder(placeOptions), [placeOptions]);
  const letters = useMemo(() => liveMapLetters(placeOrder), [placeOrder]);
  const letterOf = useCallback((placeId: string) => letters.get(placeId) ?? null, [letters]);
  const chosenPlaces = filters.places ?? [];
  const trackedIds = useMemo(() => new Set(tracked.map((person) => person.id)), [tracked]);
  const shown = useMemo(
    () => sortByName(onList.filter((person) => matchesRosterFilters(person, filters, stateOf, trackedIds.has(person.id), listedPlaceWords)), sort),
    [onList, filters, stateOf, trackedIds, sort],
  );
  /**
   * WHAT THE MAP SHOWS (#689), worked out from the people already loaded and the one filter value, and only while the Map view
   * is showing. A place's count follows every filter EXCEPT the place selection (`withoutPlaces`), so choosing one area does not
   * change the shading of the others. A place is on the map when it is in the app's own list of places, which gives its position
   * and is where its outline is fetched from (our own files, #615); an old code that is not in the list has no position and is
   * left off. No outline is given here: the map fetches each one itself and shows a pin until it arrives.
   */
  const onMap = view === 'map';
  const matchingOthers = useMemo(
    () => (onMap ? onList.filter((person) => matchesRosterFilters(person, withoutPlaces(filters), stateOf, trackedIds.has(person.id), listedPlaceWords)) : EMPTY),
    [onMap, onList, filters, stateOf, trackedIds],
  );
  /**
   * WHO THE MAP COUNTS (#804). Only staff who see patients: a direct-care job profile in the catalog. Office staff shown in the
   * list, and staff with no role set, are listed but not counted.
   */
  const coverage = useMemo(() => coverageCounts(onList, matchingOthers, { counted: seesPatients }), [onList, matchingOthers]);
  const othersOn = otherFiltersOn(filters);
  const mapPlaces = useMemo(() => {
    if (!onMap) return NO_MAP_PLACES;
    const counts = new Map(coverage.places.map((count) => [count.placeId, count] as const));
    return placeOrder.flatMap((placeId): LiveMapPlace[] => {
      const listed = findVirginiaPlace(placeId);
      const count = counts.get(placeId);
      if (listed === undefined || count === undefined) return [];
      return [{ code: placeId, name: placeWords(placeId), position: { lat: listed.lat, lng: listed.lng }, letter: letters.get(placeId) ?? '', count: countWords(count, othersOn), matches: count.matching > 0, staff: count.matching }];
    });
  }, [onMap, coverage, placeOrder, letters, othersOn]);
  const jobTitles = useMemo(() => jobTitleOptions(onList), [onList]);
  /** A press on an area or its letter: that place becomes the one chosen place. A press on the one chosen place clears it. */
  const chooseOnMap = useCallback((placeId: string) => changeFilters(choosePlace(filtersRef.current, placeId)), [changeFilters]);
  const clearOnMap = useCallback(() => changeFilters(showEveryone(filtersRef.current)), [changeFilters]);
  const controls: RosterControls = useMemo(
    () => ({ filters, onFilters: changeFilters, sort, onSort: setSort, areas, places: placeOptions, anyWithoutArea: onList.some(hasNoServiceArea), jobTitles }),
    [filters, changeFilters, sort, areas, placeOptions, onList, jobTitles],
  );
  /**
   * A service area is something the server will keep only while somebody on the roster holds it ("AR strict"),
   * and it compares them the way this does. The no-area choice is always fine.
   */
  const areaInUse = useCallback((area: string) => area === ROSTER_NO_AREA || areas.some((used) => sameServiceArea(used, area)), [areas]);
  const rosterLoaded = people.isSuccess;
  const parts = useMemo(() => toPreference(filters), [filters]);
  const partsKey = keyOf(parts);
  const canSave = preferenceIsOn(parts);

  // On open: the last filters come back once they arrive, unless the person already changed something. If they never arrive the roster is unfiltered and usable.
  useEffect(() => {
    // Waits for the roster too: whether an area is still in use cannot be told before the people have arrived.
    if (!canEdit || openHandled || !preferences.loaded || !rosterLoaded) return;
    setOpenHandled(true);
    const last = preferences.view?.screens.roster.last ?? null;
    if (touched.current || last === null) return;
    const read = readStored(last, areaInUse);
    if (read.dropped.length > 0) setNote({ text: staleMessage('last', read.dropped, read.nothingLeft), key: keyOf(read.nothingLeft ? NONE : read.parts) });
    if (!preferenceIsOn(read.parts)) return;
    setFilters((current) => withParts(current, read.parts));
    setApplied(read.parts);
  }, [canEdit, openHandled, preferences.loaded, preferences.view, rosterLoaded, areaInUse]);

  // An area that nobody holds any more (the roster changed under an open page) is left out and said so, so it is never sent to be refused.
  useEffect(() => {
    if (!canEdit || !rosterLoaded) return;
    const gone = filters.areas.filter((area) => !areaInUse(area));
    if (gone.length === 0) return;
    const next = { ...filters, areas: filters.areas.filter(areaInUse) };
    setFilters(next);
    setNote({ text: areaGoneMessage(gone), key: keyOf(toPreference(next)) });
  }, [canEdit, rosterLoaded, filters, areaInUse]);

  // The strip says the filters are the remembered ones, so it goes the moment any of the four parts changes, and stays gone for the visit.
  useEffect(() => {
    if (applied !== null && !samePreference(parts, applied)) setStripGone(true);
  }, [parts, applied]);

  // Remember what is on, after a pause. Never before the stored ones have been read (that could overwrite them with an empty set), and the search box is not part of `parts`.
  const stored = preferences.view?.screens.roster.last ?? null;
  const storedKey = keyOf(stored === null ? NONE : readStored(stored).parts);
  const pending = useRef<{ readonly parts: RosterFilterPreference; readonly key: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ send: preferences.setLast });
  latest.current = { send: preferences.setLast };
  /**
   * Send the waiting change now. There is a waiting change only while an admin who is signed in has a change that
   * differs from what is stored: the effect below clears it the moment either stops being true, so flushing
   * cannot write after sign-out or when nothing changed.
   */
  const flush = useCallback(() => {
    if (timer.current !== null) { clearTimeout(timer.current); timer.current = null; }
    const waiting = pending.current;
    pending.current = null;
    if (waiting !== null) void latest.current.send(waiting.parts).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (timer.current !== null) { clearTimeout(timer.current); timer.current = null; }
    if (!canEdit || !openHandled || !preferences.loaded || partsKey === storedKey) { pending.current = null; return; }
    pending.current = { parts, key: partsKey };
    timer.current = setTimeout(flush, LAST_USED_SAVE_DELAY_MS);
    // `parts` changes identity every render; `partsKey` carries its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, openHandled, preferences.loaded, partsKey, storedKey, flush]);
  // Leaving the roster, or the page going to the background, inside the pause must not lose the last change.
  useEffect(() => {
    const hidden = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [flush]);

  /** A saved filter replaces the four saved parts and leaves the search box; a part no longer allowed is left out and said so. */
  const applySaved = (item: SavedRosterFilter) => {
    const read = readStored(item.filters, areaInUse);
    touched.current = true;
    if (read.nothingLeft) { setNote({ text: staleMessage('saved', read.dropped, true), key: partsKey }); return; }
    setFilters((current) => withParts(current, read.parts));
    setNote(read.dropped.length > 0 ? { text: staleMessage('saved', read.dropped, false), key: keyOf(read.parts) } : null);
  };
  const atLimit = (preferences.view?.screens.roster.saved.length ?? 0) >= MAX_SAVED_FILTERS;
  const savedFilterControls = (saveThese: () => void) => !canEdit ? null : preferences.loaded ? (
    <SavedFiltersMenu preferences={preferences} filters={filters} areaInUse={areaInUse} onApply={applySaved} onSaveThese={saveThese} />
  ) : preferences.failed ? (
    <Caption1>Saved filters are not available right now.</Caption1>
  ) : null;
  /** What ends the filter row. Nothing at all for a Viewer, so the row gets no empty column. */
  const rowEnd = savedFilterControls(() => setSaving('elsewhere')) ?? undefined;
  /**
   * What ends the Filters button's panel: the same control, but "Save these filters" there closes the panel first and opens the
   * dialog once it has gone, so the dialog is never over an open panel. Nothing for a Viewer.
   */
  const panelEnd = !canEdit ? undefined : (panel: CompactFiltersPanel) => savedFilterControls(() => panel.closeThen(() => setSaving('panel')));
  const saveLink = canEdit && preferences.loaded ? (
    <>
      <Button appearance="transparent" disabled={!canSave || atLimit} onClick={() => setSaving('elsewhere')}>Save these filters</Button>
      {atLimit ? <Caption1>{LIMIT_MESSAGE}</Caption1> : null}
    </>
  ) : null;

  const activeCount = employed.filter((person) => person.lifecycle === 'active').length;
  const inactiveCount = employed.length - activeCount;
  const untrackedCount = employed.length - tracked.length;

  /**
   * Counted only once readiness has settled. A band showing 0 while its checklists are still
   * resolving reads as a fact ("nobody needs review") rather than as "not known yet", and that is
   * the number an HR admin would act on.
   */
  const counts = useMemo(() => {
    const tally = new Map<ReadinessState, number>();
    let unresolved = 0;
    for (const person of tracked) {
      const state = stateOf(person.id);
      // A FAILED checklist is not a person with no readiness. `settled` is true once every request
      // has arrived OR failed, so without counting these the bands would drop the loading placeholder
      // and show four confident figures that quietly sum to less than the roster.
      if (state === null) { unresolved += 1; continue; }
      tally.set(state, (tally.get(state) ?? 0) + 1);
    }
    return { tally, unresolved };
  }, [tracked, stateOf]);

  /**
   * Nothing is asserted about the roster until the roster has actually arrived. `employed` is empty
   * while the request is in flight and when it fails, so a subtitle derived from its length alone
   * stated "Nobody is employed yet" as a fact — directly above the spinner, and beside the error bar.
   */
  const context = people.isPending
    ? 'Loading the roster.'
    : people.isError
      ? 'The roster could not be loaded.'
      : employed.length === 0
        // Without Hiring in this build, "when they move to Active" names a screen HR does not have (#722).
        ? (sectionOn('hiring') ? 'Nobody is employed yet. Add the staff you already have, or people become roster staff when they move to Active.' : 'Nobody is employed yet. Add the staff you already have.')
        : `${activeCount} active ${activeCount === 1 ? 'employee' : 'employees'}${inactiveCount > 0 ? ` · ${inactiveCount} inactive (on leave)` : ''}${showCompliance && untrackedCount > 0 ? ` · ${untrackedCount} not tracked yet` : ''}`;

  const remembered = canEdit && applied !== null && !stripGone ? <RememberedStrip /> : null;
  const staleNote = canEdit && note !== null && note.key === partsKey ? <MessageBar intent="warning"><MessageBarBody>{note.text}</MessageBarBody></MessageBar> : null;
  const heading = staffHeading({ filters, shown: shown.length, total: onList.length, sort, placeLabelOf: placeWords, complianceLabel });
  const sideColumn = !onMap ? null : (
    <StaffSideList
      key="side"
      people={shown}
      entryOf={entryOf}
      onOpen={openQuickLook}
      profileHref={profileHrefOf}
      letterOf={letterOf}
      titleOf={titleOf}
      chosenPlaces={chosenPlaces}
      heading={heading}
      sort={sort}
      onSort={setSort}
      onShowEveryone={clearOnMap}
      onClearFilters={() => changeFilters(noRosterFilters)}
      noPlace={coverage.noPlace}
      filtered={othersOn}
      onShowNoPlace={filters.noPlace === true ? undefined : () => changeFilters(showNoPlace(filters))}
      fills={!stacked}
      narrow={!stacked && !sideWide}
      top={
        <>
          <RosterCompactFilters controls={controls} trailing={panelEnd} buttonRef={filtersButton} />
          {remembered === null && staleNote === null && officeCount === 0 ? null : <div className={styles.sideNotice}>{remembered}{staleNote}{officeLine}</div>}
        </>
      }
    />
  );
  const mapColumn = !onMap ? null : (
    <div key="map" className={styles.mapBox} data-map-box="">
      {mapPlaces.length === 0 ? (
        <section className={styles.noMap} aria-label={NO_PLACES_ON_MAP_TITLE}>
          <PinLetter letter={null} />
          <Text weight="semibold" size={400}>{NO_PLACES_ON_MAP_TITLE}</Text>
          <div className={styles.noMapWords}>
            <Text>{NO_PLACES_ON_MAP_BODY}</Text>
            <Caption1>{NO_PLACES_ON_MAP_NOTE}</Caption1>
          </div>
        </section>
      ) : (
        <LiveMap places={mapPlaces} chosen={chosenPlaces} onChoose={chooseOnMap} onClear={clearOnMap} notes={MAP_NOTES} nobody={MAP_NOBODY_COUNTED} />
      )}
    </div>
  );

  return (
    <div className={mergeClasses(styles.page, onMap && !stacked && employed.length > 0 && styles.pageFills)}>
      <header className={styles.headRow}>
        <div className={styles.head}>
          <h1 className={styles.title}>Roster</h1>
          <Caption1>{context}</Caption1>
        </div>
        <RosterViewSwitch view={view} onView={changeView} />
        {canEdit ? <Button appearance="primary" onClick={(event) => openDialog(null, event.currentTarget)}>Add staff member</Button> : null}
      </header>
      {separated === null ? null : (
        <MessageBar intent="success">
          <MessageBarBody>{separated} marked Separated.</MessageBarBody>
          <MessageBarActions><Button onClick={() => setSeparated(null)}>Dismiss</Button></MessageBarActions>
        </MessageBar>
      )}

      {/* The five count boxes are on the Table view only (Julio's choice 2 A, #648): in the Map view the map starts under the title. */}
      {showCompliance && tracked.length > 0 && !onMap ? (
        <>
          <section className={styles.bands} aria-label="Roster readiness">
            {BANDS.map((state) => (
              <div key={state} className={styles.cell}>
                <span className={styles.figure} aria-busy={!readiness.settled}>
                  {readiness.settled ? counts.tally.get(state) ?? 0 : '…'}
                </span>
                <span className={styles.label}>{statePresenters[state].label}</span>
              </div>
            ))}
          </section>
          {readiness.settled && counts.unresolved > 0 ? (
            // Said out loud rather than absorbed: the bands below sum to less than the roster, and a
            // silent shortfall is indistinguishable from "those people are fine".
            <MessageBar intent="warning">
              <MessageBarBody>
                {counts.unresolved} of {tracked.length} checklists could not be read, so the bands above do not cover everyone.
              </MessageBarBody>
              <MessageBarActions>
                <Button onClick={() => void people.refetch()}>Try again</Button>
              </MessageBarActions>
            </MessageBar>
          ) : null}
        </>
      ) : null}

      {/* The Table view's filters: the row of fields, or on a phone Search and one Filters button (frame T3). The Map view's are inside its roster side. */}
      {employed.length > 0 && !onMap ? (tier === 'mobile' ? <RosterCompactFilters controls={controls} trailing={panelEnd} buttonRef={filtersButton} searchPlaceholder="Name, email, phone or place" /> : <RosterFilterRow controls={controls} trailing={rowEnd} />) : null}
      {employed.length > 0 && !onMap ? remembered : null}
      {employed.length > 0 && !onMap ? staleNote : null}
      {employed.length > 0 && !onMap ? <ActiveFilters controls={controls} shown={shown.length} total={onList.length} trailing={saveLink} /> : null}
      {employed.length > 0 && !onMap ? officeLine : null}

      {people.isPending ? (
        <div className={styles.centered}>
          <Spinner size="small" label="Loading the roster" />
        </div>
      ) : people.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>Could not load the roster. Try again in a moment.</MessageBarBody>
          <MessageBarActions>
            <Button onClick={() => void people.refetch()}>Try again</Button>
          </MessageBarActions>
        </MessageBar>
      ) : employed.length === 0 ? (
        <section className={styles.card} aria-label="Nobody on the roster">
          <Text weight="semibold">No employees yet.</Text>
          <Caption1>{sectionOn('hiring')
            ? 'Add the staff you already have, or people appear here once they move to Active. Candidates and people still onboarding are under Hiring.'
            : 'Add the staff you already have.'}</Caption1>
          {canEdit ? <Button appearance="primary" onClick={(event) => openDialog(null, event.currentTarget)}>Add staff member</Button> : null}
        </section>
      ) : onMap ? (
        <div ref={measureMapView} className={stacked ? styles.mapViewStacked : mergeClasses(styles.mapView, sideWidth === 'narrow' && styles.mapViewNarrow, sideWidth === 'slim' && styles.mapViewSlim)} data-map-view={stacked ? 'stacked' : sideWidth}>
          {/* Keyed, so the two change places (the map first when stacked) without either being made again. */}
          {(stacked ? [mapColumn, sideColumn] : [sideColumn, mapColumn])}
        </div>
      ) : (
        <div className={styles.who}>
          {shown.length === 0 ? (
            <section className={styles.card} aria-label="No matches">
              <Text weight="semibold">Nobody matches.</Text>
              <Caption1>Try removing a filter or searching with different words.</Caption1>
              <Button onClick={() => changeFilters(noRosterFilters)}>Clear all filters</Button>
            </section>
          ) : (
            <StaffTable
              people={shown}
              entryOf={entryOf}
              onEdit={canEdit ? openDialog : undefined}
              onOpen={openQuickLook}
              profileHref={profileHrefOf}
              letterOf={letterOf}
              chosenPlaces={chosenPlaces}
              controls={controls}
              mapOnPage
            />
          )}
        </div>
      )}

      {canEdit && preferences.loaded ? <SaveFiltersDialog open={saving !== null} preferences={preferences} parts={parts} onClose={() => setSaving(null)} opener={saving === 'panel' ? filtersButton : undefined} /> : null}
      {/* Looked up by id on every draw, so the panel shows the record as the roster holds it now, and closes if the person leaves it. */}
      <QuickLookPanel person={employed.find((person) => person.id === looking) ?? null} opener={lookOpener} onClose={() => setLooking(null)} />
      <StaffDialog key={opened} person={editing ?? null} open={editing !== undefined} opener={staffOpener} onClose={() => setEditing(undefined)} />
    </div>
  );
}
